import { useRef, useEffect, useState, useCallback } from "react";

const COLS = 10;
const ROWS = 20;
const CELL = 22;
const W = COLS * CELL;
const H = ROWS * CELL;

const SHAPES = {
  I: { color: "#6B8EAC", cells: [[0, 1], [1, 1], [2, 1], [3, 1]] },
  O: { color: "#D4AF37", cells: [[1, 0], [2, 0], [1, 1], [2, 1]] },
  T: { color: "#6A0DAD", cells: [[1, 0], [0, 1], [1, 1], [2, 1]] },
  S: { color: "#4CAF50", cells: [[1, 0], [2, 0], [0, 1], [1, 1]] },
  Z: { color: "#E05252", cells: [[0, 0], [1, 0], [1, 1], [2, 1]] },
  J: { color: "#5A7BD8", cells: [[0, 0], [0, 1], [1, 1], [2, 1]] },
  L: { color: "#E0872E", cells: [[2, 0], [0, 1], [1, 1], [2, 1]] },
};
const KEYS = Object.keys(SHAPES);

function emptyGrid() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(null));
}
function randomPiece() {
  const k = KEYS[Math.floor(Math.random() * KEYS.length)];
  return { cells: SHAPES[k].cells.map((c) => [...c]), color: SHAPES[k].color, x: 3, y: 0 };
}
function rotate(cells) {
  // rotate 90deg around approximate center
  return cells.map(([x, y]) => [-y, x]).map(([x, y]) => [x + 2, y]);
}

export default function BlocksGame({ onGameOver }) {
  const canvasRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [score, setScore] = useState(0);
  const g = useRef(null);

  const collides = (grid, piece, cells = piece.cells, ox = piece.x, oy = piece.y) => {
    for (const [cx, cy] of cells) {
      const x = ox + cx;
      const y = oy + cy;
      if (x < 0 || x >= COLS || y >= ROWS) return true;
      if (y >= 0 && grid[y][x]) return true;
    }
    return false;
  };

  const merge = (grid, piece) => {
    piece.cells.forEach(([cx, cy]) => {
      const y = piece.y + cy;
      const x = piece.x + cx;
      if (y >= 0) grid[y][x] = piece.color;
    });
  };

  const clearLines = (grid) => {
    let cleared = 0;
    for (let y = ROWS - 1; y >= 0; y--) {
      if (grid[y].every((c) => c)) {
        grid.splice(y, 1);
        grid.unshift(Array(COLS).fill(null));
        cleared++;
        y++;
      }
    }
    return cleared;
  };

  const draw = useCallback(() => {
    const ctx = canvasRef.current.getContext("2d");
    const s = g.current;
    ctx.fillStyle = "#1a0b2e";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255,255,255,0.05)";
    for (let x = 0; x <= COLS; x++) { ctx.beginPath(); ctx.moveTo(x * CELL, 0); ctx.lineTo(x * CELL, H); ctx.stroke(); }
    for (let y = 0; y <= ROWS; y++) { ctx.beginPath(); ctx.moveTo(0, y * CELL); ctx.lineTo(W, y * CELL); ctx.stroke(); }
    const cell = (x, y, color) => {
      ctx.fillStyle = color;
      ctx.fillRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2);
    };
    if (!s) return;
    for (let y = 0; y < ROWS; y++)
      for (let x = 0; x < COLS; x++)
        if (s.grid[y][x]) cell(x, y, s.grid[y][x]);
    if (s.piece)
      s.piece.cells.forEach(([cx, cy]) => cell(s.piece.x + cx, s.piece.y + cy, s.piece.color));
  }, []);

  const start = () => {
    g.current = { grid: emptyGrid(), piece: randomPiece(), scoreVal: 0 };
    setScore(0);
    setOver(false);
    setRunning(true);
  };

  const endGame = useCallback(() => {
    const finalScore = g.current ? g.current.scoreVal : 0;
    setRunning(false);
    setOver(true);
    onGameOver(finalScore);
  }, [onGameOver]);

  const step = useCallback(() => {
    const s = g.current;
    if (!s || !s.piece) return;
    if (!collides(s.grid, s.piece, s.piece.cells, s.piece.x, s.piece.y + 1)) {
      s.piece.y += 1;
    } else {
      merge(s.grid, s.piece);
      const cleared = clearLines(s.grid);
      if (cleared) {
        s.scoreVal += [0, 100, 300, 500, 800][cleared];
        setScore(s.scoreVal);
      }
      s.piece = randomPiece();
      if (collides(s.grid, s.piece)) {
        endGame();
        return;
      }
    }
    draw();
  }, [draw, endGame]);

  useEffect(() => {
    if (!running) return;
    draw();
    const interval = setInterval(step, 600);

    const onKey = (e) => {
      const s = g.current;
      if (!s || !s.piece) return;
      if (["ArrowLeft", "ArrowRight", "ArrowDown", "ArrowUp", " "].includes(e.key)) e.preventDefault();
      if (e.key === "ArrowLeft" && !collides(s.grid, s.piece, s.piece.cells, s.piece.x - 1, s.piece.y)) s.piece.x -= 1;
      else if (e.key === "ArrowRight" && !collides(s.grid, s.piece, s.piece.cells, s.piece.x + 1, s.piece.y)) s.piece.x += 1;
      else if (e.key === "ArrowDown") { if (!collides(s.grid, s.piece, s.piece.cells, s.piece.x, s.piece.y + 1)) { s.piece.y += 1; s.scoreVal += 1; setScore(s.scoreVal); } }
      else if (e.key === "ArrowUp") {
        const r = rotate(s.piece.cells);
        if (!collides(s.grid, s.piece, r, s.piece.x, s.piece.y)) s.piece.cells = r;
      }
      draw();
    };
    window.addEventListener("keydown", onKey);
    return () => { clearInterval(interval); window.removeEventListener("keydown", onKey); };
  }, [running, step, draw]);

  const control = (dir) => {
    const s = g.current;
    if (!s || !s.piece) return;
    if (dir === "left" && !collides(s.grid, s.piece, s.piece.cells, s.piece.x - 1, s.piece.y)) s.piece.x -= 1;
    if (dir === "right" && !collides(s.grid, s.piece, s.piece.cells, s.piece.x + 1, s.piece.y)) s.piece.x += 1;
    if (dir === "down" && !collides(s.grid, s.piece, s.piece.cells, s.piece.x, s.piece.y + 1)) { s.piece.y += 1; s.scoreVal += 1; setScore(s.scoreVal); }
    if (dir === "rotate") { const r = rotate(s.piece.cells); if (!collides(s.grid, s.piece, r, s.piece.x, s.piece.y)) s.piece.cells = r; }
    draw();
  };

  return (
    <div className="flex flex-col items-center">
      <canvas ref={canvasRef} width={W} height={H} data-testid="blocks-canvas"
        className="rounded-xl border border-[#6A0DAD] bg-[#1a0b2e]" />
      <p className="font-heading text-sm text-[#4A5568] mt-3">Score: <span className="font-bold text-[#6A0DAD]" data-testid="blocks-score">{score}</span></p>
      {running ? (
        <div className="flex gap-2 mt-3 sm:hidden">
          {[["left", "←"], ["rotate", "⟳"], ["down", "↓"], ["right", "→"]].map(([d, l]) => (
            <button key={d} onClick={() => control(d)} className="w-12 h-12 rounded-xl bg-[#6A0DAD] text-white text-lg font-bold">{l}</button>
          ))}
        </div>
      ) : (
        <button data-testid="blocks-start-btn" onClick={start} className="mt-3 bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-6 py-2.5 font-heading font-semibold transition-colors">
          {over ? "Play again" : "Start"}
        </button>
      )}
      <p className="text-xs text-[#4A5568] mt-2">Arrow keys: move · ↑ rotate · ↓ drop. Clear lines to score!</p>
    </div>
  );
}
